/**
 * Live Update (OTA) — 관리자 제어형.
 *
 * 설계: docs/84_OTA_LIVE_UPDATE_DESIGN.md
 *
 * 흐름:
 *   1. 부팅 직후 notifyAppReady() — 현재 번들 정상 통지 (미호출 시 자동 롤백).
 *   2. BE GET /app/version-policy 로 디바이스별 정책 조회 (판단은 BE).
 *   3. mode 별 처리:
 *      - SILENT    : 백그라운드 download → next() (다음 cold start 적용). UI 없음.
 *      - PROMPT    : download → next() 예약 + 사용자 안내(지금/나중에).
 *      - MANDATORY : download → 닫기 불가 게이트(사용자가 "지금 업데이트" → set()).
 *      - storeUpdate : 네이티브 셸이 낡음 → 스토어 이동 안내.
 *   4. 다운로드/적용/서명검증/롤백은 @capgo/capacitor-updater 가 담당.
 *
 * web 환경에서는 no-op (브라우저는 일반 배포로 즉시 갱신).
 */
import { isNative, getPlatform, nativeWarn } from "./index";
import { apiGet, apiPost } from "../api/client";
import { env } from "../config/env";
import { useOtaStore } from "../store/otaStore";
import { useAuthStore } from "../store/authStore";
import {
  clearOtaApplyingReadyPending,
  markOtaApplyingReadyPending,
} from "./otaApplyingState";

/** BE version-policy 응답 (duckflex-peeka-be OtaDto.VersionPolicy 와 1:1). */
export interface OtaPolicy {
  updateAvailable: boolean;
  storeUpdateRequired: boolean;
  /** 강제(차단) 스토어 업데이트 — 앱 버전 정책 MIN_REQR_VER 미달. */
  storeUpdateMandatory: boolean;
  mode: "SILENT" | "PROMPT" | "MANDATORY" | null;
  version: string | null;
  url: string | null;
  sessionKey: string | null;
  checksum: string | null;
  size: number | null;
  minNativeVersion: string | null;
  releaseNotes: string | null;
  /** 스토어 업데이트 커스텀 안내 메시지(선택). */
  storeNoticeMessage: string | null;
  /**
   * 후원(건전지) 기능이 열려 있는가. OTA 와 무관한 값이지만, 앱이 부팅·복귀마다 부르는
   * 유일한 공개 GET 이라 여기에 실린다(PLAN_BATTERY_IAP §11.3.1).
   * ⚠ 옵셔널이다 — 이 필드가 없는 옛 BE 도 있다(`api/donation` 의 조회가 그때 false 로 읽는다).
   */
  donationEnabled?: boolean;
}

const DEVICE_ID_KEY = "template-ota-device-id";
/** 이미 다운로드+예약(또는 안내)한 OTA 버전 — 같은 버전 재안내/재다운로드 방지. */
const HANDLED_VERSION_KEY = "template-ota-handled-version";
/** 마지막 수신 영수증 보고 {version, member} — 멱등 가드(중복 POST 방지). */
const RECEIPT_KEY = "template-ota-receipt";
/**
 * 스토어 URL — env 주입 (config/env.ts).
 * App Store ID 미설정 시 빈 문자열 → openAppStore 가 이동을 막는다.
 * (플레이스홀더 URL 로 404 게이트에 사용자를 가두는 사고 방지)
 */
const APP_STORE_URL = env.VITE_APP_STORE_ID
  ? `https://apps.apple.com/app/id${env.VITE_APP_STORE_ID}`
  : "";
const PLAY_STORE_URL =
  `https://play.google.com/store/apps/details?id=${env.VITE_PLAY_STORE_PACKAGE || "kr.duckflex.peeka.app"}`;

const BUILTIN_VERSION =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.0.0";

/** UI 표시용 현재 번들 버전 캐시 (sync getter). init 에서 채움. */
let currentVersionCache: string | null = null;
/** UI 표시용 현재 네이티브 셸 버전 캐시 (sync getter) — 스토어 앱 버전. init 에서 채움. */
let currentNativeVersionCache: string | null = null;

function getOtaChannel(): string {
  try {
    return (import.meta.env.VITE_OTA_CHANNEL as string | undefined)?.toUpperCase() ?? "INTERNAL";
  } catch {
    return "INTERNAL";
  }
}

/**
 * 안정적 deviceId — Preferences 에 1회 생성 저장.
 *
 * 원래 용도는 단계 배포 코호트 산정이지만, 푸시 기기 등록의 <b>설치 식별자</b>로도 같은 값을
 * 쓴다(fcmPush). 저장 키 이름이 OTA 를 가리키는 건 유래일 뿐이고 값의 뜻은 "이 설치본" 이다 —
 * 키를 바꾸면 이미 깔린 기기의 값이 날아가 기기 행이 새로 생기므로 그대로 둔다.
 */
export async function getDeviceId(): Promise<string | null> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const got = await Preferences.get({ key: DEVICE_ID_KEY });
    if (got.value) return got.value;
    const id =
      (globalThis.crypto?.randomUUID?.() as string | undefined) ??
      `dev-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
    await Preferences.set({ key: DEVICE_ID_KEY, value: id });
    return id;
  } catch (err) {
    nativeWarn("ota:deviceId", err);
    return null;
  }
}

/** 이미 처리(다운로드+예약/안내)한 OTA 버전 조회. */
async function getHandledVersion(): Promise<string | null> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    return (await Preferences.get({ key: HANDLED_VERSION_KEY })).value;
  } catch {
    return null;
  }
}

/** 처리한 OTA 버전 기록 (reload/cold start 사이에도 유지). */
async function setHandledVersion(version: string): Promise<void> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key: HANDLED_VERSION_KEY, value: version });
  } catch (err) {
    nativeWarn("ota:handled", err);
  }
}

/** 처리 기록 삭제 (적용 완료 시). */
async function clearHandledVersion(): Promise<void> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.remove({ key: HANDLED_VERSION_KEY });
  } catch {
    /* noop */
  }
}

/** 현재 번들/네이티브 버전 (capgo). web 이면 builtin fallback. */
async function readVersions(): Promise<{ bundle: string; native: string }> {
  if (!isNative()) {
    currentVersionCache = BUILTIN_VERSION;
    currentNativeVersionCache = BUILTIN_VERSION;
    return { bundle: BUILTIN_VERSION, native: BUILTIN_VERSION };
  }
  try {
    const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
    const cur = await CapacitorUpdater.current();
    // ⚠ 빌트인 번들은 capgo 가 version 을 네이티브 versionName(예: "1.0.0")으로 보고한다.
    //   이는 OTA 번들 버전 라인(YYYY.MMDD.HHMMSS)과 달라 비교가 깨지고(1 < 2026) 옛 번들로
    //   되돌려지므로, 빌트인일 때는 빌드 시 박은 __APP_VERSION__(같은 날짜 라인)을 보고한다.
    //   다운로드된 OTA 번들은 download({version}) 로 박힌 BNDL_VER 을 그대로 쓴다.
    const isBuiltin = cur.bundle?.id === "builtin" || !cur.bundle?.version;
    currentVersionCache = isBuiltin ? BUILTIN_VERSION : cur.bundle.version;
    currentNativeVersionCache = cur.native || null;
    return { bundle: currentVersionCache, native: cur.native };
  } catch (err) {
    nativeWarn("ota:current", err);
    currentVersionCache = BUILTIN_VERSION;
    return { bundle: BUILTIN_VERSION, native: "0.0.0" };
  }
}

/** UI 표시용 현재 OTA 번들 버전 (sync) = FE 배포 버전. 정확한 값은 init 후 캐시. */
export function getCurrentOtaVersion(): string | null {
  return currentVersionCache ?? BUILTIN_VERSION;
}

/**
 * UI 표시용 현재 네이티브 셸 버전 (sync) = 스토어 앱 버전.
 * init(readVersions) 전이거나 web 환경이면 null. init 후 캐시.
 */
export function getCurrentNativeVersion(): string | null {
  return currentNativeVersionCache;
}

/**
 * UI 표시용 현재 실행 중 FE 번들의 빌드 버전 (sync) = OTA/배포 버전.
 *
 * 빌드 시 vite define 으로 박힌 __APP_VERSION__ 을 그대로 돌려준다.
 *   - 빌트인 APK 면 그 APK 의 빌드 버전, OTA 적용 후엔 그 OTA 번들의 빌드 버전
 *     (값이 각 번들 JS 에 박혀 있으므로 실행 중 번들 기준으로 항상 정확).
 *   - capgo current() 의 builtin 번들 version 오보고(네이티브 versionName 으로 보고)에
 *     영향받지 않는다 — readVersions/currentVersionCache 와 독립.
 */
export function getBuildVersion(): string {
  return BUILTIN_VERSION;
}

/**
 * BE 에 정책 조회. 새 번들이 있으면 OtaPolicy 반환 (없으면 updateAvailable=false).
 * DebugMenu/Settings 의 수동 "강제 체크" 에서도 호출 (다운로드 트리거하지 않음).
 */
export async function checkForOtaUpdate(): Promise<OtaPolicy | null> {
  try {
    const { bundle, native } = await readVersions();
    const deviceId = isNative() ? await getDeviceId() : null;
    const policy = await apiGet<OtaPolicy>("/app/version-policy", {
      channel: getOtaChannel(),
      currentVersion: bundle,
      nativeVersion: native,
      deviceId,
      platform: getPlatform(),
    });
    return policy;
  } catch (err) {
    nativeWarn("ota:policy", err);
    return null;
  }
}

/** capgo download — sessionKey/checksum 있으면 암호화 번들로 전달. 번들 id 반환. */
async function downloadBundle(policy: OtaPolicy): Promise<string | null> {
  if (!policy.url || !policy.version) return null;
  try {
    const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
    const opts: { url: string; version: string; sessionKey?: string; checksum?: string } = {
      url: policy.url,
      version: policy.version,
    };
    if (policy.sessionKey) opts.sessionKey = policy.sessionKey;
    if (policy.checksum) opts.checksum = policy.checksum;
    const bundle = await CapacitorUpdater.download(opts);
    return bundle.id;
  } catch (err) {
    nativeWarn("ota:download", err);
    return null;
  }
}

/** 다음 cold start 에 적용되도록 예약. */
async function stageNext(bundleId: string): Promise<void> {
  try {
    const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
    await CapacitorUpdater.next({ id: bundleId });
  } catch (err) {
    nativeWarn("ota:next", err);
  }
}

/**
 * 즉시 적용 (WebView reload). 사용자 컨펌/강제 시 UI 에서 호출.
 *
 * 성공 시 set() 이 reload 를 트리거 → 이후 코드는 보통 실행되지 않음(true 는 best-effort).
 * 실패 시 false 반환 → 호출부(UpdatePrompt)가 "업데이트 중" 오버레이를 풀고 에러 안내.
 * (false 를 안 돌려주면 applying 이 영구 true 로 남아 오버레이가 멈춘다.)
 */
export async function applyOtaBundle(bundleId: string, version?: string | null): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
    markOtaApplyingReadyPending(version ?? bundleId);
    await CapacitorUpdater.set({ id: bundleId });
    return true;
  } catch (err) {
    clearOtaApplyingReadyPending();
    nativeWarn("ota:set", err);
    return false;
  }
}

/** 스토어로 이동 (네이티브 셸 업데이트 필요 시). URL 미설정 플랫폼은 no-op + 경고. */
export async function openAppStore(): Promise<void> {
  const url = getPlatform() === "ios" ? APP_STORE_URL : PLAY_STORE_URL;
  if (!url) {
    nativeWarn("ota:openAppStore", new Error("store URL not configured (VITE_APP_STORE_ID)"));
    return;
  }
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url });
  } catch {
    window.open(url, "_blank");
  }
}

/**
 * OTA 수신 영수증 보고 (CS 추적) — 현재 실행 중 번들을 BE 에 1회 기록.
 *
 * 설계 docs/84 §14. 회원번호는 body 로 보내지 않는다 — BE 가 토큰(있으면)에서 해석.
 * memberSerialNo 인자는 **로컬 멱등 가드**(재보고 판단)에만 쓰인다.
 *   - 비로그인(부팅): member=null → 버전당 1회 익명 보고.
 *   - 로그인 복구 후: member=회원번호 → (버전,회원)당 1회 stamp 보고.
 * 실패는 무시(best-effort) — version-policy 와 분리된 경로라 부팅/핫패스에 영향 없음.
 */
export async function reportOtaReceipt(memberSerialNo: number | null): Promise<void> {
  if (!isNative()) return;
  const { bundle } = await readVersions();
  const deviceId = await getDeviceId();
  if (!deviceId || !bundle) return;

  try {
    const { Preferences } = await import("@capacitor/preferences");
    const raw = (await Preferences.get({ key: RECEIPT_KEY })).value;
    const last = raw ? (JSON.parse(raw) as { version: string; member: number | null }) : null;
    const sameVersion = last?.version === bundle;

    // 멱등 가드 — 익명은 버전당 1회, 회원 stamp 는 (버전,회원)당 1회.
    // 이미 회원 stamp 된 버전에 익명 재보고가 끼어들지 않도록 분기.
    if (memberSerialNo == null) {
      if (sameVersion) return; // 이 버전은 이미 보고됨(익명이든 회원이든)
    } else if (sameVersion && last?.member === memberSerialNo) {
      return; // 같은 회원으로 이미 stamp 됨
    }

    await apiPost<void>("/app/ota/receipt", { deviceId, bundleVersion: bundle });
    await Preferences.set({
      key: RECEIPT_KEY,
      value: JSON.stringify({ version: bundle, member: memberSerialNo }),
    });
  } catch (err) {
    nativeWarn("ota:receipt", err);
  }
}

/**
 * 프로세스(콜드스타트)당 1회만 서기 — 재부팅 후 첫 체크인지 판별용.
 * "이미 처리(handled)"로 기록된 버전인데 재부팅 후에도 currentVersionCache 가
 * 그 버전과 다르면(=예약된 번들이 적용 안 됨: capgo 롤백 등) 재부팅 후 첫 체크에서만
 * handled 기록을 무시하고 재시도한다. 같은 프로세스 내 반복 체크(앱 재개 등)에서는
 * 사용자가 "나중에"를 눌러 아직 재시작 전인 경우를 재안내로 스팸하지 않도록 그대로 존중한다.
 */
let bootCheckDone = false;

/** 정책 1건 처리 — 모드별 다운로드/예약/안내. */
async function handlePolicy(policy: OtaPolicy | null): Promise<void> {
  const isFirstCheckThisBoot = !bootCheckDone;
  bootCheckDone = true;

  if (!policy) return;

  if (policy.storeUpdateRequired) {
    useOtaStore
      .getState()
      .showStoreUpdate(policy.storeUpdateMandatory, policy.version, policy.storeNoticeMessage);
    return;
  }
  if (!policy.updateAvailable || !policy.version) return;

  // 멱등성 ①: 이미 그 버전을 실행 중이면 적용 완료 — 안내/다운로드 안 함.
  // (BE 가 같은 버전을 계속 updateAvailable=true 로 응답해도 방어.)
  if (currentVersionCache && policy.version === currentVersionCache) {
    await clearHandledVersion();
    return;
  }

  // 멱등성 ②: 같은 버전을 이미 다운로드+예약(또는 안내)했으면 재처리 안 함.
  // MANDATORY 는 닫기 불가 게이트이므로 매번 표시(예약 없이 사용자 적용 대기).
  if (policy.mode !== "MANDATORY") {
    const handled = await getHandledVersion();
    if (handled === policy.version) {
      // 재부팅 후 첫 체크인데도 버전이 안 바뀌었다면 예약된 적용이 실패(롤백)한 것 —
      // handled 기록을 무한 신뢰하면 영구히 재안내/재다운로드가 막히므로 초기화하고 재시도.
      if (isFirstCheckThisBoot) {
        await clearHandledVersion();
      } else {
        return;
      }
    }
  }

  const bundleId = await downloadBundle(policy);
  if (!bundleId) return;

  const store = useOtaStore.getState();
  switch (policy.mode) {
    case "MANDATORY":
      // 강제 — 닫기 불가 게이트. 사용자가 "지금 업데이트" 누르면 applyOtaBundle.
      store.showMandatory(bundleId, policy.version, policy.releaseNotes);
      break;
    case "PROMPT":
      // 예약(next)해 두고 안내. "나중에" 여도 다음 실행 때 적용됨.
      await stageNext(bundleId);
      await setHandledVersion(policy.version);
      store.showPrompt(bundleId, policy.version, policy.releaseNotes);
      break;
    case "SILENT":
    default:
      // 조용히 다음 실행 적용.
      await stageNext(bundleId);
      await setHandledVersion(policy.version);
      break;
  }
}

let checking = false;
async function checkAndHandle(): Promise<void> {
  if (checking) return;
  checking = true;
  try {
    await handlePolicy(await checkForOtaUpdate());
  } finally {
    checking = false;
  }
}

/**
 * OTA 부트스트랩 — main.tsx 에서 1회 호출. web 에서는 no-op.
 */
export function initLiveUpdate(): void {
  if (!isNative()) {
    currentVersionCache = BUILTIN_VERSION;
    return;
  }

  // 디버그 토글 — VITE_DISABLE_OTA=1 로 빌드하면 OTA 정책조회/다운로드/적용을 끈다.
  // (로컬/QA 빌드에서 빌트인 번들을 OTA 가 덮어쓰지 못하게 고정. 평소 빌드는 미설정이라 무영향.)
  // 단, notifyAppReady 는 그대로 호출한다 — 미호출 시 capgo 워치독이 appReadyTimeout 후
  // 빌트인을 "실패"로 보고 리로드/롤백을 시도해 테스트가 끊길 수 있으므로.
  const otaDisabled = import.meta.env.VITE_DISABLE_OTA === "1";

  void (async () => {
    // 1. 현재 번들 정상 부팅 통지 — 가장 먼저. 미호출 시 자동 롤백.
    try {
      const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
      await CapacitorUpdater.notifyAppReady();
      clearOtaApplyingReadyPending();
    } catch (err) {
      nativeWarn("ota:notifyReady", err);
    }

    await readVersions();

    // OTA 비활성(QA/로컬) — 업데이트 메커니즘만 끄고 부팅 통지/버전캐시는 유지.
    if (otaDisabled) return;

    await checkAndHandle();

    // 수신 영수증 (CS 추적) — 부팅 시 익명 보고 1회.
    // 콜드스타트 인증 복구(bootstrapped+user) 후 회원번호 stamp 를 위해 authStore 구독.
    // user/bootstrapped 가 실제로 바뀐 경우에만 — silent refresh(토큰만 변경)엔 반응 안 함.
    void reportOtaReceipt(useAuthStore.getState().user?.memberSerialNo ?? null);
    useAuthStore.subscribe((s, prev) => {
      const changed = s.user !== prev.user || s.bootstrapped !== prev.bootstrapped;
      if (changed && s.bootstrapped && s.user) {
        void reportOtaReceipt(s.user.memberSerialNo);
      }
    });

    // 앱 재개 시 재확인 (백그라운드 오래 머문 뒤 복귀 등).
    try {
      const { App } = await import("@capacitor/app");
      await App.addListener("appStateChange", (state) => {
        if (state.isActive) void checkAndHandle();
      });
    } catch (err) {
      nativeWarn("ota:appState", err);
    }
  })();
}
